'use client'

import {useSidebar} from '@app/components/sidebar/sidebar-visiblity-context'
import {ReactNode, useEffect, useRef, useState} from 'react'
import s from './style.module.scss'
import Backdrop from '@app/components/header/sections/desktop/search/backdrop/backdrop'
import {useScrollLock} from '@app/hooks/use-scroll-lock'
import clsx from 'clsx'
import triggerAnalytics from '@gamezop/analytics-sdk/client/trigger-analytics'
import {LanguageCodes} from '@app/i18n/settings'
import {useTagsPageAnalytics} from '@app/[lang]/(common-header-footer)/tags/(tag-page)/[tagName]/[tagId]/get-tag-page-analytics/tag-page-analytics-context'
import DesktopWideMonitorSidebar from './wide-monitor-sidebar/desktop-wide-monitor-sidebar'
import {usePathname} from 'next/navigation'
import {useIsGamePage} from '@app/[lang]/(common-header-footer)/content-wrapper/content-wrapper-client'
import {useFullScreen} from '@app/components/gameplayer-page/actions-section/buttons/full-screen/full-screen-provider'

type Props = {
  children: ReactNode
  lang: LanguageCodes
  wideContent: ReactNode
}

const DesktopSidebarVisiblityProviderClient = ({children, wideContent}: Props) => {
  const {isOpen, closeSidebar, openSidebar} = useSidebar()
  const [isHovered, setIsHovered] = useState(false)
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const {lockScroll, unlockScroll} = useScrollLock()
  const {tagAnalyticsData} = useTagsPageAnalytics()
  const analyticsSent = useRef(false)
  const path = usePathname()
  const {isGamePage} = useIsGamePage()
  const {isFullScreen} = useFullScreen()

  useEffect(() => {
    if (isGamePage && isOpen) {
      closeSidebar()
    } else if (!isGamePage && !isOpen) {
      openSidebar()
    }
  }, [path])

  const handleMouseEnter = () => {
    lockScroll()
    setIsHovered(true)
    if (!analyticsSent.current) {
      triggerAnalytics('side-menu-expanded', tagAnalyticsData).sendAll()
      analyticsSent.current = true
    }
  }

  const handleMouseLeave = () => {
    // scroll icons to top if hover removed
    sidebarRef?.current?.scrollTo(0, 0)
    setIsHovered(false)
    unlockScroll()
  }

  if (isFullScreen) return null

  return (
    <>
      <DesktopWideMonitorSidebar isOpen={isOpen}>{wideContent}</DesktopWideMonitorSidebar>
      <div className={s.hide_on_resize}>
        {isOpen && <div className='w-64' />}
        <aside
          className={clsx(s.sidebar_container, {[s.sidebar_open]: isOpen, [s.sidebar_close]: !isOpen})}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={sidebarRef}
          onClick={unlockScroll}
        >
          {children}
        </aside>
        <Backdrop showBackdrop={isHovered} onClick={closeSidebar} className={s.backdrop} />
      </div>
    </>
  )
}

export default DesktopSidebarVisiblityProviderClient
